import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import AskFilledNote from "./assistant/AskFilledNote";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { postReq, putReq } from "../utils/request";
import ConcurrencyConflictModal, { ConcurrencyConflict, ResolvedConflicts } from "./ConcurrencyConflictModal";
import Input from "./Input";
import PhoneNumberInput from "./PhoneNumberInput";
import {
    ActivityItem,
    ActivityParticipant,
    ActivityPerson,
    ActivityType,
    ActivityDirection,
    ActivityOutcome,
} from "../types/Activity";
import ActivityPeoplePicker from "./ActivityPeoplePicker";
import Button from "./Button";
import { FormGrid, FormGridItem } from "./record";
import { activityTypeLabel, followUpDayKey } from "./activityParts";

export interface ActivityFormModalProps {
    show: boolean;
    onClose: () => void;
    parentType: string;
    parentId: number;
    /** Prefill for a NEW activity (ignored while `activity` is set). */
    initial?: Partial<ActivityItem> | null;
    /** Existing activity to edit (PUT). Omit to create (POST). */
    activity?: ActivityItem | null;
    onSaved?: (activity: ActivityItem) => void;
    /** Fields Ask filled in (D9), shown at the top for the person to check. */
    askFilled?: string[];
}

export const INITIAL_ACTIVITY: Omit<ActivityItem, "parentType" | "parentId"> = {
    activityId: null,
    activityType: ActivityType.Call,
    direction: ActivityDirection.Outbound,
    subject: "",
    description: null,
    participants: [],
    contactInfo: null,
    location: null,
    meetingLink: null,
    outcome: null,
    activityDate: new Date().toISOString(),
    durationMinutes: null,
    nextFollowUpDate: null,
    nextFollowUpNotes: null,
    isFollowUpCompleted: false,
    createdBy: 0,
    createdAt: "",
};

/**
 * The activity with its dates taken from the BE's ISO twins: the plain
 * fields are server-locale strings that read shifted east of the server and
 * can't be cut to a day for the follow-up date input.
 */
const withIsoDates = (activity: ActivityItem): ActivityItem => ({
    ...activity,
    activityDate: activity.activityDateIso ?? activity.activityDate,
    nextFollowUpDate: activity.nextFollowUpDateIso
        ?? (activity.nextFollowUpDate ? followUpDayKey(activity) || activity.nextFollowUpDate : null),
});

export const getContactInfoLabel = (type: ActivityType): string => {
    switch (type) {
        case ActivityType.Call:
        case ActivityType.WhatsApp:
            return "Phone Number";
        case ActivityType.Email:
            return "Email Address";
        case ActivityType.LinkedIn:
        case ActivityType.ConnectionRequest:
        case ActivityType.InMail:
        case ActivityType.ProfileView:
            return "LinkedIn Profile URL";
        default:
            return "Contact Info";
    }
};

/**
 * Add / edit one activity (call, meeting, note …) on any polymorphic parent.
 * Owns the draft, the save round-trip and the concurrency conflict flow so the
 * activity list and the comment quick actions share one form.
 */
const ActivityFormModal: React.FC<ActivityFormModalProps> = ({
    show,
    onClose,
    parentType,
    parentId,
    initial,
    activity,
    onSaved,
    askFilled,
}) => {
    const isEditing = !!activity && activity.activityId != null;
    const [draft, setDraft] = useState<ActivityItem>({ ...INITIAL_ACTIVITY, parentType, parentId });
    const [isSaving, setIsSaving] = useState(false);
    // Snapshot at edit-open time — feeds the BE auto-merge and the conflict
    // modal's "Original" column.
    const originalRef = useRef<ActivityItem | null>(null);
    const [conflicts, setConflicts] = useState<ConcurrencyConflict[]>([]);

    useEffect(() => {
        if (!show) return;
        if (activity) {
            const normalized = withIsoDates(activity);
            setDraft({ ...normalized, parentType, parentId });
            originalRef.current = JSON.parse(JSON.stringify(normalized));
        } else {
            setDraft({ ...INITIAL_ACTIVITY, activityDate: new Date().toISOString(), ...(initial ?? {}), parentType, parentId });
            originalRef.current = null;
        }
        setConflicts([]);
        setIsSaving(false);
    }, [show, activity, initial, parentType, parentId]);

    const handleChange = (name: string, value: string | boolean | object) => {
        setDraft((prev) => ({ ...prev, [name]: value }));
    };

    /** The people it was with; the first one picked also fills an empty phone / email. */
    const handleParticipantsChange = (participants: ActivityParticipant[], added: ActivityPerson | null) => {
        setDraft((prev) => {
            const next = { ...prev, participants };
            if (!added || prev.contactInfo) return next;
            const isPhoneType = prev.activityType === ActivityType.Call || prev.activityType === ActivityType.WhatsApp;
            const reach = isPhoneType ? added.phone : prev.activityType === ActivityType.Email ? added.email : null;
            return reach ? { ...next, contactInfo: reach } : next;
        });
    };

    const buildPayload = (forceOverwrite = false, overwriteReason?: string, clientMutationId?: string): any => {
        const payload: any = { ...draft, parentType, parentId };
        payload.clientMutationId = clientMutationId ?? uuidv4();
        if (forceOverwrite) payload.forceOverwrite = true;
        if (overwriteReason) payload.overwriteReason = overwriteReason;
        if (originalRef.current) payload.originalSnapshot = originalRef.current;
        return payload;
    };

    const finishSaved = (body: any, message: string) => {
        toast.success(message);
        const saved: ActivityItem = body && typeof body === "object" && body.activityId != null ? { ...draft, ...body } : draft;
        onSaved?.(saved);
        onClose();
    };

    const handleSave = async () => {
        if (!draft.subject || !draft.subject.trim()) {
            toast.error("Subject is required");
            return;
        }
        if (isSaving) return;
        setIsSaving(true);
        const payload = isEditing ? buildPayload() : { ...draft, parentType, parentId };
        const response = isEditing
            ? await putReq(`activities/${activity!.activityId}`, payload)
            : await postReq("activities", payload);
        if (response.status === 200) {
            finishSaved(response.body, isEditing ? "Activity updated successfully" : "Activity added successfully");
        } else if (isEditing && response.status === 409 && response.body?.error === "CONCURRENCY_CONFLICT") {
            setConflicts(response.body.conflicts ?? []);
        } else {
            toast.error(isEditing ? "Failed to update activity" : "Failed to add activity");
        }
        setIsSaving(false);
    };

    /** Conflict-modal lookups — Activity has no cascade children. */
    const lookupDraftRow = (entityName: string, _id: number): any | undefined => {
        if (entityName === "activity") return draft;
        return undefined;
    };
    const lookupOriginalRow = (entityName: string, _id: number): any | undefined => {
        if (entityName === "activity") return originalRef.current ?? undefined;
        return undefined;
    };

    const handleConflictResolve = async (resolved: ResolvedConflicts) => {
        let nextDraft: any = JSON.parse(JSON.stringify(draft));
        const original = originalRef.current;

        for (const [key, decision] of Array.from(resolved.decisions.entries())) {
            const canonical = resolved.canonicalById.get(key);
            if (!canonical) continue;
            const [entityName] = key.split(":");
            if (entityName !== "activity") continue;

            if (decision.kind === "accept-theirs") {
                nextDraft = { ...nextDraft, ...canonical };
            } else if (decision.kind === "accept-both" && original) {
                const merged: any = { ...nextDraft };
                const skip = new Set(["version", "createdAt", "updatedAt", "deletedAt"]);
                const allKeys = Array.from(new Set<string>([...Object.keys(canonical), ...Object.keys(original)]));
                for (const field of allKeys) {
                    if (skip.has(field)) continue;
                    const userChanged = JSON.stringify((nextDraft as any)[field]) !== JSON.stringify((original as any)[field]);
                    if (!userChanged) merged[field] = canonical[field];
                }
                nextDraft = merged;
            }
        }

        setDraft(nextDraft);
        setConflicts([]);

        const payload = buildPayload(true, resolved.overwriteReason);
        Object.assign(payload, nextDraft, {
            parentType, parentId,
            clientMutationId: payload.clientMutationId,
            forceOverwrite: true,
            overwriteReason: resolved.overwriteReason,
            originalSnapshot: payload.originalSnapshot,
        });

        const response = await putReq(`activities/${activity!.activityId}`, payload);
        if (response.status === 200) {
            finishSaved(response.body, "Activity saved (conflict resolved).");
        } else {
            toast.error("Failed to save resolved activity");
        }
    };

    const showLocationField = [ActivityType.Meeting].includes(draft.activityType);
    const showMeetingLinkField = [ActivityType.Meeting, ActivityType.Call].includes(draft.activityType);
    const activityDateValue = draft.activityDate
        ? new Date(new Date(draft.activityDate).getTime() - new Date(draft.activityDate).getTimezoneOffset() * 60000).toISOString().substring(0, 16)
        : "";

    return (
        <>
            <ConcurrencyConflictModal
                show={conflicts.length > 0}
                conflicts={conflicts}
                userDraftLookup={lookupDraftRow}
                originalLookup={lookupOriginalRow}
                onResolve={handleConflictResolve}
                onCancel={() => setConflicts([])}
            />
            <Modal show={show} onHide={onClose} centered size="lg" fullscreen="sm-down">
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? "Edit activity" : "Log an activity"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {askFilled && !activity && <AskFilledNote fields={askFilled} />}
                    <FormGrid>
                        <FormGridItem>
                            <Input type="select" label="Type" id="activityType" value={draft.activityType} onChange={handleChange}
                                options={Object.values(ActivityType).map((t) => ({ value: t, label: activityTypeLabel(t) }))}
                                required tooltip="Call, email, meeting, note…" />
                        </FormGridItem>
                        <FormGridItem>
                            <Input type="select" label="Direction" id="direction" value={draft.direction || ""} onChange={handleChange}
                                options={Object.values(ActivityDirection).map((d) => ({ value: d, label: d.charAt(0).toUpperCase() + d.slice(1) }))}
                                tooltip="Outbound = you reached out; inbound = they did" />
                        </FormGridItem>
                        <FormGridItem>
                            <Input type="datetime-local" label="Date and time" id="activityDate"
                                value={activityDateValue}
                                onChange={handleChange} className="form-control" required />
                        </FormGridItem>
                        <FormGridItem wide>
                            <Input type="text" label="Subject" id="subject" value={draft.subject} onChange={handleChange} required
                                tooltip="One line people will recognise in the timeline" />
                        </FormGridItem>
                        <FormGridItem wide>
                            <ActivityPeoplePicker parentType={parentType} parentId={parentId}
                                value={draft.participants ?? []} onChange={handleParticipantsChange} />
                        </FormGridItem>
                        <FormGridItem wide>
                            <Input type="textarea" label="What happened" id="description" value={draft.description || ""} onChange={handleChange} rows={3} />
                        </FormGridItem>
                        <FormGridItem>
                            {(draft.activityType === ActivityType.Call || draft.activityType === ActivityType.WhatsApp) ? (
                                <PhoneNumberInput
                                    value={draft.contactInfo || ""}
                                    onChange={(value: string) => handleChange("contactInfo", value)}
                                />
                            ) : (
                                <Input type="text" label={getContactInfoLabel(draft.activityType)} id="contactInfo" value={draft.contactInfo || ""} onChange={handleChange} />
                            )}
                        </FormGridItem>
                        <FormGridItem>
                            <Input type="select" label="Outcome" id="outcome" value={draft.outcome || ""} onChange={handleChange}
                                options={Object.values(ActivityOutcome).map((o) => ({ value: o, label: o.charAt(0).toUpperCase() + o.slice(1) }))}
                                tooltip="How it went — leave empty when it doesn't apply"
                            />
                        </FormGridItem>
                        {showLocationField && (
                            <FormGridItem>
                                <Input type="text" label="Location" id="location" value={draft.location || ""} onChange={handleChange} />
                            </FormGridItem>
                        )}
                        {showMeetingLinkField && (
                            <FormGridItem>
                                <Input type="text" label="Meeting link" id="meetingLink" value={draft.meetingLink || ""} onChange={handleChange} />
                            </FormGridItem>
                        )}
                        <FormGridItem>
                            <Input type="number" label="Duration (min)" id="durationMinutes" value={draft.durationMinutes} onChange={handleChange} min={0} />
                        </FormGridItem>
                        <FormGridItem>
                            <Input type="date" label="Next follow-up" id="nextFollowUpDate" value={draft.nextFollowUpDate ? draft.nextFollowUpDate.substring(0, 10) : ""} onChange={handleChange} className="form-control"
                                tooltip="Set a day to come back to it — it shows under Follow-ups due and on My Calendar" />
                        </FormGridItem>
                        {draft.nextFollowUpDate && (
                            <FormGridItem wide>
                                <Input type="textarea" label="Follow-up notes" id="nextFollowUpNotes" value={draft.nextFollowUpNotes || ""} onChange={handleChange} rows={2} />
                            </FormGridItem>
                        )}
                    </FormGrid>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="ghost" onClick={onClose} disabled={isSaving} title="Close without saving">Cancel</Button>
                    <Button variant="primary" icon="check2" onClick={handleSave} disabled={isSaving} loading={isSaving} loadingText="Saving…"
                        title={isEditing ? "Save the changes" : "Log this activity"}>
                        {isEditing ? "Save changes" : "Log activity"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ActivityFormModal;
